import {useSyncExternalStore} from 'react';
import {message} from '../../lib/api';
import {queryCache} from '../../lib/queryCache';
import {submitTrash,UnconfirmedTrashError} from './trashRequest';
import type {Content} from '../../lib/types';

type Removal={keys:string[];pending:boolean;unconfirmed?:boolean;item:Content};
type State={removals:Removal[];error:string;notice:string};
const empty:State={removals:[],error:'',notice:''};
let state=empty;
const listeners=new Set<()=>void>();
const subscribe=(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};};
const snapshot=()=>state;
function update(next:State){state=next;listeners.forEach(listener=>listener());}
export const draftIds=(item:Content)=>item.kind==='article'?(item.articleIds||[item.articleId||item.id]):[item.id];
const source=(item:Content)=>item.kind==='social'?'social':item.source;
const keys=(item:Content)=>draftIds(item).map(id=>`${source(item)}:${id}`);

// Keep pending deletions across navigation and clear them when the session changes.
queryCache.subscribe(event=>{if(event==='session'||event==='denied')update(empty);});

export function useOptimisticTrash(){
 const current=useSyncExternalStore(subscribe,snapshot);
 const hidden=new Set(current.removals.flatMap(removal=>removal.keys));
 return {...current,unconfirmed:current.removals.filter(removal=>removal.unconfirmed),pending:current.removals.some(removal=>removal.pending),visible:(item:Content)=>!keys(item).some(key=>hidden.has(key))};
}

export function trashDraft(item:Content){
 const itemKeys=keys(item);
 const previous=state.removals.find(removal=>removal.keys.some(key=>itemKeys.includes(key)));
 if(previous&&!previous.unconfirmed)return;
 const removal:Removal={keys:itemKeys,pending:true,item},epoch=queryCache.sessionEpoch;
 const isCurrent=()=>epoch===queryCache.sessionEpoch&&state.removals.includes(removal);
 update({...state,removals:[...state.removals.filter(current=>current!==previous),removal],error:'',notice:''});
 void submitTrash({source:source(item),ids:draftIds(item),resourceId:item.resourceId},epoch,!!previous,isCurrent).then(()=>{
  if(!isCurrent())return;
  update({...state,removals:state.removals.map(current=>current===removal?{...current,pending:false}:current),notice:'Kladden er flyttet til papirkurven og kan gendannes i 30 dage.'});
 }).catch(error=>{
  if(!isCurrent())return;
  if(error instanceof UnconfirmedTrashError){
   update({...state,removals:state.removals.map(current=>current===removal?{...current,pending:false,unconfirmed:true}:current),notice:''});
   return;
  }
  update({...state,notice:'',removals:state.removals.filter(current=>current!==removal),error:`“${item.title||'Uden titel'}” kunne ikke flyttes til papirkurven. Kladden vises igen. ${message(error)}`});
 });
}

// Retain the mask until a refreshed list confirms the deletion, avoiding a flash
// of the old card between the mutation response and the list response.
export function reconcileTrashedDrafts(items:Content[]){
 const present=new Set(items.flatMap(keys));
 const removals=state.removals.filter(removal=>removal.pending||removal.unconfirmed||removal.keys.some(key=>present.has(key)));
 if(removals.length!==state.removals.length)update({...state,removals});
}

export function restoredDraft(source:string,id:string){
 const key=`${source}:${id}`;
 const removals=state.removals.map(removal=>removal.keys.includes(key)?{...removal,keys:removal.keys.filter(value=>value!==key)}:removal).filter(removal=>removal.keys.length);
 update({...state,removals,notice:''});
}
